// fallow-ignore-file unused-file
import { GetStaticPaths, GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { useTranslation } from 'next-i18next';

import { Crumble, Environment } from '@boss/types/b2b-b2c';
import {
  appendValueToSlugsObject,
  getLocalizedSlugForEventPage,
  getLocalizedSlugsForEventPage,
  slugify,
} from '@boss/utils';

import { EventCardWrapper, SEO } from '../../../components';
import { PageTypes } from '../../../constants';
import Layout from '../../../layout/default';
import { resolveEventMarketContext } from '../../../utils/eventMarket';
import getServerSideTranslations from '../../../utils/getServerSideTranslations';
import { mapContentfulToNextLocale } from '../../../utils/localeMapper';
import { getMappedNavigation } from '../../../utils/navigation';
import { eventType } from '../../../utils/options';
import { filterTrainingsByType } from '../../../utils/trainings';
import { loadTrainings } from '../../../utils/trainings/server';

// fallow-ignore-next-line complexity -- standard Next.js page shell, identical to every other page in apps/site.
export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  if (!locales || eventType !== 'TRAINING') {
    return {
      paths: [],
      fallback: 'blocking',
    };
  }

  const paths = [];

  for (const locale of locales.filter((locale: string) => locale !== 'default')) {
    const events = await loadTrainings(locale);
    const types = new Set(events.map(event => event.type?.trim()).filter((type): type is string => !!type));

    for (const type of types) {
      paths.push({ locale: mapContentfulToNextLocale(locale), params: { topic: slugify(type) } });
    }
  }

  return {
    paths,
    fallback: 'blocking',
  };
};

// fallow-ignore-next-line complexity -- standard Next.js page shell, identical to every other page in apps/site.
export const getStaticProps = async ({ preview, locale, params }: GetStaticPropsContext) => {
  const topic = params?.topic as string | undefined;

  if (!locale || locale === 'default' || !topic || eventType !== 'TRAINING') {
    return {
      notFound: true,
    };
  }

  const { contentfulLocaleMarket, market } = resolveEventMarketContext(locale);

  if (!contentfulLocaleMarket || !market) {
    return {
      notFound: true,
    };
  }

  const [navigation, events] = await Promise.all([
    getMappedNavigation(contentfulLocaleMarket, !!preview),
    loadTrainings(locale),
  ]);

  const sessions = filterTrainingsByType(events, topic);

  if (!sessions.length) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      pageType: PageTypes.EVENT_OVERVIEW,
      topic,
      trainingType: sessions[0].type.trim(),
      events: sessions,
      locale,
      preview: !!preview,
      navigation,
      environment: process.env.CONTENTFUL_ENVIRONMENT as Environment,
      ...(await getServerSideTranslations(locale, ['common', 'event', 'workshop', 'forms', 'routes'])),
    },
    revalidate: 3600,
  };
};

export const Index = ({
  topic,
  trainingType,
  events,
  navigation,
  locale,
  preview,
  environment,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation('event');
  const localizedSlugs = appendValueToSlugsObject(`/topic/${topic}`, getLocalizedSlugsForEventPage());
  const crumbles: Crumble[] = [
    { href: getLocalizedSlugForEventPage(locale), label: t('pageTitle', { ns: 'event' }) },
    { href: getLocalizedSlugForEventPage(locale, `topic/${topic}`), label: trainingType },
  ];

  return (
    <SEO
      breadcrumbs={crumbles}
      description={trainingType}
      localizedSlugs={localizedSlugs}
      navigation={navigation}
      title={trainingType}
    >
      <Layout
        crumbles={crumbles}
        environment={environment}
        localizedSlugs={localizedSlugs}
        navigation={navigation}
        preview={preview}
      >
        {/* ponytail: topic-page content model pending in Contentful (45259) — heading + sessions only for now */}
        <h3 className="mb-5">{trainingType}</h3>
        <div className="my-10 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          {events.map(event => (
            <EventCardWrapper event={event} key={event.id} locale={locale} />
          ))}
        </div>
      </Layout>
    </SEO>
  );
};

export default Index;
